import { useMemo } from 'react';
import { VoiceChannelMember } from 'src/provider/VoiceChannel';
import { useLivekitRTCState } from 'src/provider/VoiceChannel/livekit/useLivekitRTCState';
import { MAX_GRID_SIZE, MAX_USERS, MAX_VISIBLE_USERS } from './constants';

const calculateGrid = (totalUsers: number) => {
  const cols = Math.min(
    MAX_GRID_SIZE,
    Math.max(1, totalUsers === 1 ? 1 : Math.ceil(Math.sqrt(totalUsers))),
  );
  const rows = Math.min(
    MAX_GRID_SIZE,
    Math.max(1, Math.ceil(totalUsers / cols)),
  );
  return { cols, rows };
};

export const useVoiceChannelMembersGrid = ({
  conversationId,
  participants,
}: { conversationId: string; participants: VoiceChannelMember[] }) => {
  const {
    activeVoiceChannel,
    connectionStatus,
    theaterMember,
    activeMember,
    myMeetId,
    viewMode,
  } = useLivekitRTCState();

  const connected =
    connectionStatus !== 'disconnected' &&
    activeVoiceChannel?.channel?._id === conversationId;

  const { cols, rows } = calculateGrid(participants.length);

  const participantsWithoutMe = useMemo(
    () => participants.filter((item) => item.meetId !== myMeetId),
    [participants, myMeetId],
  );

  const listView = connected && (viewMode === 'list' || !!theaterMember);

  const theaterMemberParticipant = useMemo(
    () => participants.find((member) => member.meetId === theaterMember),
    [participants, theaterMember],
  );

  const activeMemberOrSomeone = useMemo(
    () => activeMember || participantsWithoutMe?.[0]?.meetId || myMeetId,
    [activeMember, participantsWithoutMe, myMeetId],
  );

  const listViewActiveParticipant = useMemo(() => {
    if (viewMode === 'grid') return theaterMemberParticipant;

    return (
      theaterMemberParticipant ||
      participants.find((member) => member.meetId === activeMemberOrSomeone)
    );
  }, [viewMode, theaterMemberParticipant, participants, activeMemberOrSomeone]);

  const participantsWithoutTheaterMember = useMemo(() => {
    if (!listViewActiveParticipant) return participants;
    return participants.filter(
      (member) => member.meetId !== listViewActiveParticipant.meetId,
    );
  }, [listViewActiveParticipant, participants]);

  const extraUsers = useMemo(
    () => (listView ? 0 : participants.length - MAX_VISIBLE_USERS),
    [listView, participants.length],
  );

  const visibleUsers = useMemo(
    () =>
      listView || participants.length <= MAX_USERS
        ? participantsWithoutTheaterMember
        : participantsWithoutTheaterMember.slice(0, MAX_VISIBLE_USERS),
    [listView, participants.length, participantsWithoutTheaterMember],
  );

  return {
    cols,
    rows,
    extraUsers,
    visibleUsers,
    activeMemberOrSomeone,
    connected,
    listView,
    listViewActiveParticipant,
    participantsWithoutMe,
  };
};
